import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Pencil, Check, Shuffle } from 'lucide-react';

import { TopBar, Btn, Pill, Avatar, AVATAR_LIST, DashboardPage, IconButton, Page, isPhotoAvatar } from '../components/wr';
import { useGame } from '../context/GameContext';
import { useAuth } from '../context/AuthContext';
import { generateGuestName } from '../services/firebase';

/**
 * Profile / Setup — same component handles both routes:
 *   `/setup`    — first-time setup, always shows the picker.
 *   `/profile`  — profile summary; tap "Edit" to enter picker mode.
 *
 * Avatar state is deliberately TWO values, not one:
 *   `brandedId`  — the branded pick ('fox', 'pine', …), always retained
 *   `useGoogle`  — whether to display the Google photo instead
 *
 * They're kept apart because collapsing them into a single field is what
 * broke the Google photo in the first place: this screen's default pick
 * ('fox') would overwrite the photo on the very first save, with no way
 * back. Now the two coexist and the toggle is lossless in both directions.
 */
export default function ProfileScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useGame();
  const { currentUser, userProfile, updateUserProfile, signOut, isGuest } = useAuth();

  // Default to "view" mode on /profile; "edit" mode on /setup or /profile/edit
  const isSetupRoute = location.pathname.startsWith('/setup') || location.pathname.includes('/edit') || location.pathname.includes('/new');
  const hasExistingProfile = !!(userProfile?.displayName);
  const [mode, setMode] = useState<'view' | 'edit'>(isSetupRoute || !hasExistingProfile ? 'edit' : 'view');

  const [name, setName] = useState<string>('');
  const [brandedId, setBrandedId] = useState<string>('fox');
  const [useGoogle, setUseGoogle] = useState<boolean>(false);
  const [bio, setBio] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // The Google photo, from the live auth user or the mirror we persisted.
  const photoURL: string | undefined =
    (currentUser as any)?.photoURL || userProfile?.photoURL || undefined;

  // The avatar actually rendered — derived, exactly like the stored
  // `profile.avatar` is derived. One rule, one place.
  const effectiveAvatar = useGoogle && photoURL ? photoURL : brandedId;

  useEffect(() => {
    // "Player" is not a name — it's the fallback string that every layer of
    // this app reaches for when it has nothing. Treat it as nothing, or the
    // user gets shown a placeholder as if it were their own choice and has to
    // work out that it isn't. Guests who came through before the profile-race
    // fix have literally no stored displayName; give them a real one to save.
    const real = (v?: string | null) => (v && v.trim() && v.trim() !== 'Player' ? v.trim() : null);
    setName(
      real(userProfile?.displayName) ??
      real(currentUser?.displayName) ??
      real(state.currentPlayer?.name) ??
      generateGuestName(),
    );

    // Hydrate from the reconciled fields written by AuthContext. Fall back
    // through the legacy shapes for a profile that hasn't been reconciled yet.
    const storedBranded =
      userProfile?.brandedAvatar ||
      (!isPhotoAvatar(userProfile?.avatar) ? userProfile?.avatar : undefined) ||
      (!isPhotoAvatar(state.currentPlayer?.avatar) ? state.currentPlayer?.avatar : undefined) ||
      'fox';
    setBrandedId(storedBranded);

    const hasPhoto = !!((currentUser as any)?.photoURL || userProfile?.photoURL);
    setUseGoogle(userProfile?.useGooglePhoto ?? (hasPhoto && isPhotoAvatar(userProfile?.avatar)) ?? false);

    setBio(userProfile?.bio || '');
  }, [userProfile, currentUser, state.currentPlayer]);

  const trimmedName = name.trim();
  const canSave = trimmedName.length >= 2 && trimmedName.length <= 24;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      dispatch({
        type: 'SET_PLAYER',
        payload: {
          id: state.currentPlayer?.id || currentUser?.uid || `guest-${Date.now()}`,
          name: trimmedName,
          avatar: effectiveAvatar,
          isReady: false,
          isHost: false,
          score: 0,
        },
      });
      if (currentUser && updateUserProfile) {
        await updateUserProfile({
          displayName: trimmedName,
          // `avatar` stays the derived, effective value — it's what every
          // other surface reads (lobby, socket handshake, gameRecords, the
          // results screen). The two inputs that produce it are stored
          // alongside so the choice survives.
          avatar: effectiveAvatar,
          brandedAvatar: brandedId,
          useGooglePhoto: useGoogle,
          ...(photoURL ? { photoURL } : {}),
          bio: bio.slice(0, 140),
        });
      }
      if (isSetupRoute) {
        navigate('/home');
      } else {
        setMode('view');
      }
    } catch (e) {
      console.error('Failed to save profile', e);
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      if (signOut) await signOut();
    } catch {}
    navigate('/login');
  };

  const handleBack = () => {
    if (mode === 'edit' && !isSetupRoute) {
      setMode('view');
    } else {
      navigate(state.currentPlayer ? '/home' : '/');
    }
  };

  // ===== View mode =====
  if (mode === 'view') {
    return (
      /* The dashboard template: identity + the five "how am I doing" views
         share one frame above 1024. Below it, the phone's single column and
         its menu, untouched. */
      <DashboardPage
        top={
          <TopBar
            left={<IconButton icon="ChevronLeft" ariaLabel="Back" onClick={handleBack} />}
            center="Profile"
            right={<IconButton icon="Pencil" ariaLabel="Edit profile" onClick={() => setMode('edit')} />}
          />
        }
      >

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="wr-col gap-4"
            style={{ flex: 1 }}
          >
            {/* Profile header. The name was previously an .h2 dropped into a
                flex column with no width constraint — a long display name
                ran to the container edge and read as left-drifting against
                the centred avatar. Constrain it, centre the text explicitly,
                and let it wrap instead of colliding with the padding. */}
            <div className="wr-col wr-center gap-2 mt-4" style={{ width: '100%', padding: '0 8px' }}>
              <Avatar id={effectiveAvatar} size={120} fallbackId={brandedId} />
              <h2
                className="h2"
                style={{
                  marginTop: 12,
                  textAlign: 'center',
                  maxWidth: '100%',
                  overflowWrap: 'anywhere',
                  lineHeight: 1.15,
                }}
              >
                {trimmedName || 'Player'}
              </h2>
              {currentUser?.email && (
                <div
                  className="wr-text-3"
                  style={{
                    fontSize: 13,
                    textAlign: 'center',
                    maxWidth: '100%',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {currentUser.email}
                </div>
              )}
              {useGoogle && photoURL && (
                <Pill kind="ghost" icon="ImageIcon">Using your Google photo</Pill>
              )}
              {isGuest && (
                <Pill kind="ghost" icon="UserRound">Guest — tap edit to rename</Pill>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
              {[
                { label: 'GAMES', value: userProfile?.stats?.totalGames || 0, to: '/history' },
                { label: 'WINS', value: userProfile?.stats?.totalWins || 0, to: '/history' },
                { label: 'STREAK', value: userProfile?.stats?.currentStreak || 0, to: '/stats' },
              ].map(s => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => navigate(s.to)}
                  className="card tappable"
                  style={{ textAlign: 'center', padding: '14px 10px', border: 'none', minHeight: 44, cursor: 'pointer' }}
                >
                  <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 24 }}>{s.value}</div>
                  <div className="eyebrow" style={{ fontSize: 10 }}>{s.label}</div>
                </button>
              ))}
            </div>

            <div className="wr-col gap-2 mt-4">
              <Btn kind="ghost" full icon="BarChart3" onClick={() => navigate('/stats')}>Stats</Btn>
              <Btn kind="ghost" full icon="History" onClick={() => navigate('/history')}>Game history</Btn>
              <Btn kind="ghost" full icon="Trophy" onClick={() => navigate('/achievements')}>Achievements</Btn>
              <Btn kind="ghost" full icon="Medal" onClick={() => navigate('/leaderboard')}>Leaderboard</Btn>
              <Btn kind="ghost" full icon="Sparkles" onClick={() => navigate('/plans')}>WordRush Plus</Btn>
              <Btn kind="ghost" full icon="Pencil" onClick={() => setMode('edit')}>Edit profile</Btn>
              <Btn kind="ghost" full icon="Settings2" onClick={() => navigate('/settings')}>Settings</Btn>
              {currentUser && (
                <Btn kind="ghost" full icon="LogOut" onClick={handleSignOut}>Sign out</Btn>
              )}
            </div>
          </motion.div>
      </DashboardPage>
    );
  }

  // ===== Edit mode =====
  /* NOT the dashboard template. This is a FORM — a name and an avatar — and
     it is also the /setup route, where a brand-new player lands. A 1080px
     split with a "Leaderboard" link beside a first-run setup form would be
     both silly and hostile. A narrow, focused column is the right answer for
     a form at every width. */
  return (
    <Page variant="narrow" className="wr-col">
        <TopBar
          left={<IconButton icon="ChevronLeft" ariaLabel="Back" onClick={handleBack} />}
          center={isSetupRoute ? 'Set up' : 'Edit'}
        />

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="wr-col gap-4"
          style={{ flex: 1, overflowY: 'auto', paddingBottom: 96 }}
        >
          <div className="wr-col gap-2 mt-3">
            <Pill icon="User">{isSetupRoute ? "You're in" : 'Your profile'}</Pill>
            <h2 className="h2 mt-3">{isSetupRoute ? 'Pick a face.' : 'Looking sharp.'}</h2>
            <p className="wr-text-3" style={{ fontSize: 15, marginTop: 4 }}>
              Or change your name.
            </p>
          </div>

          {/* ===== Name ===== */}
          <div className="mt-4">
            <div className="wr-between" style={{ marginBottom: 8 }}>
              <span className="eyebrow">Your name</span>
              {/* Re-roll. Guests land with a server-assigned name like
                  "Witty Fox"; without this they'd have to invent one by
                  hand, and the ones who don't bother are exactly the ones
                  who show up in everyone else's lobby as "Player". */}
              <button
                type="button"
                onClick={() => setName(generateGuestName())}
                aria-label="Shuffle name"
                className="pill ghost"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  minHeight: 32,
                  padding: '6px 12px',
                  fontSize: 12,
                  cursor: 'pointer',
                  color: 'var(--accent)',
                }}
              >
                <Shuffle size={13} /> Shuffle
              </button>
            </div>
            {/* `.field` centres its children now (it used to be baseline-
                aligned for the in-game answer input, which left this input's
                trailing pencil icon floating off the text baseline). */}
            <div className="field text">
              <input
                className="field-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Witty Fox"
                maxLength={24}
                aria-label="Display name"
              />
              <Pencil size={16} color="var(--ink-3)" style={{ flexShrink: 0 }} />
            </div>
            {!canSave && trimmedName.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6, paddingLeft: 2 }}>
                Names are 2–24 characters.
              </div>
            )}
          </div>

          {/* ===== Bio ===== */}
          <div className="mt-4">
            <div className="wr-between" style={{ marginBottom: 8 }}>
              <span className="eyebrow">Bio</span>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{bio.length} / 140</span>
            </div>
            <div className="field text" style={{ height: 'auto', alignItems: 'flex-start', padding: '14px 16px' }}>
              <textarea
                className="field-input"
                value={bio}
                onChange={e => setBio(e.target.value.slice(0, 140))}
                placeholder="A short line for your profile (optional)"
                rows={2}
                aria-label="Bio"
                style={{
                  resize: 'none',
                  fontFamily: 'var(--ui)',
                  fontSize: 14,
                  fontWeight: 500,
                  letterSpacing: 0,
                  lineHeight: 1.4,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  width: '100%',
                  color: 'var(--ink)',
                }}
              />
            </div>
          </div>

          {/* ===== Avatar ===== */}
          <div className="mt-4">
            <div className="eyebrow" style={{ marginBottom: 10 }}>Avatar</div>

            {/* Currently-selected, big. */}
            <div className="wr-row wr-center" style={{ marginBottom: 14 }}>
              <Avatar id={effectiveAvatar} size={96} fallbackId={brandedId} />
            </div>

            {/* The Google-photo option, rendered as a real choice you can
                see and select — not a claim in a settings row. Only shown
                when there IS a photo to use. */}
            {photoURL && (
              <button
                type="button"
                onClick={() => setUseGoogle(v => !v)}
                aria-pressed={useGoogle}
                className="card tappable"
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  marginBottom: 12,
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  minHeight: 44,
                  background: useGoogle ? 'var(--accent-soft)' : 'var(--surface)',
                  boxShadow: useGoogle
                    ? '0 0 0 2px var(--accent)'
                    : '0 0 0 1px var(--line), 0 1px 2px rgba(22, 22, 26, 0.04)',
                }}
              >
                <Avatar id={photoURL} size={40} fallbackId={brandedId} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Use my Google photo</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    {useGoogle ? 'On — this is what everyone sees' : 'Off — showing your branded avatar'}
                  </div>
                </div>
                {useGoogle && (
                  <span
                    aria-hidden
                    className="wr-center"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: 'var(--accent)',
                      color: '#fff',
                      flexShrink: 0,
                    }}
                  >
                    <Check size={13} strokeWidth={3} />
                  </span>
                )}
              </button>
            )}

            <div className="avgrid">
              {AVATAR_LIST.map(a => {
                // A branded tile is "selected" only when we're not showing
                // the photo — otherwise two things would look chosen at once.
                const selected = !useGoogle && brandedId === a.id;
                return (
                  <button
                    key={a.id}
                    className={`av-cell ${selected ? 'selected' : ''}`}
                    onClick={() => {
                      setBrandedId(a.id);
                      // Picking a face IS the opt-out. Explicit, not inferred.
                      setUseGoogle(false);
                    }}
                    type="button"
                    aria-label={a.name}
                    aria-pressed={selected}
                  >
                    <Avatar id={a.id} size={56} />
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: 'linear-gradient(180deg, transparent 0%, var(--bg) 30%, var(--bg) 100%)',
            paddingTop: 16,
            paddingBottom: 8,
            marginTop: 'auto',
            zIndex: 10,
          }}
        >
          <Btn
            kind="primary"
            size="xl"
            full
            icon="ArrowRight"
            disabled={!canSave}
            loading={saving}
            onClick={handleSave}
          >
            {isSetupRoute ? 'Looks good' : 'Save changes'}
          </Btn>
        </div>
    </Page>
  );
}
